import { DEFAULT_SETTINGS, createProject, newId } from "./defaults";
import {
  PricingBackupV1Schema,
  PricingProjectExportV1Schema,
  type AppState,
  type PricingBackupV1,
  type PricingProject,
  type PricingProjectExportV1,
} from "./types";

export const STORAGE_KEY = "outter-demo-calc:v1";

export function initialState(): AppState {
  const project = createProject();
  return { settings: structuredClone(DEFAULT_SETTINGS), projects: [project], activeProjectId: project.id, savedAt: new Date().toISOString() };
}

export function loadState(storage: Pick<Storage, "getItem"> = localStorage): AppState {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return initialState();
  try {
    const parsed = PricingBackupV1Schema.parse(JSON.parse(raw));
    const activeProjectId = parsed.projects.some((project) => project.id === parsed.activeProjectId)
      ? parsed.activeProjectId
      : parsed.projects[0].id;
    return { settings: parsed.settings, projects: parsed.projects, activeProjectId, savedAt: parsed.exportedAt };
  } catch {
    return initialState();
  }
}

export function toBackup(state: AppState): PricingBackupV1 {
  return {
    kind: "outter-demo-calc-backup",
    schemaVersion: 1,
    exportedAt: new Date().toISOString(),
    settings: state.settings,
    projects: state.projects,
    activeProjectId: state.activeProjectId,
  };
}

export function saveState(state: AppState, storage: Pick<Storage, "setItem"> = localStorage) {
  storage.setItem(STORAGE_KEY, JSON.stringify(toBackup(state)));
}

export function parseImport(input: unknown): PricingBackupV1 | PricingProjectExportV1 {
  const backup = PricingBackupV1Schema.safeParse(input);
  if (backup.success) return backup.data;
  const project = PricingProjectExportV1Schema.safeParse(input);
  if (project.success) return project.data;
  throw new Error("文件不是受支持的报价项目或完整备份，或其版本不兼容。 ");
}

export function cloneProject(project: PricingProject, suffix = "（副本）"): PricingProject {
  const now = new Date().toISOString();
  return {
    ...structuredClone(project),
    id: newId(),
    projectName: `${project.projectName}${suffix}`,
    quoteNumber: `${project.quoteNumber}-COPY`,
    wbsItems: project.wbsItems.map((item) => ({ ...item, id: newId() })),
    addonItems: project.addonItems.map((item) => ({ ...item, id: newId() })),
    createdAt: now,
    updatedAt: now,
  };
}

export function mergeImportedProjects(existing: PricingProject[], incoming: PricingProject[]) {
  const ids = new Set(existing.map((project) => project.id));
  const additions = incoming.map((project) => (ids.has(project.id) ? cloneProject(project, "（导入）") : project));
  return [...existing, ...additions];
}

export function downloadJson(payload: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
